// Third-party
import React from 'react';
import chroma from 'chroma-js';

// Components
import CorrelationChartRow from './CorrelationChartRow';
import Tooltip from '../../tooltips/Tooltip';
import { GenericEmptyState } from '../../misc';

// Utils
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { CORRELATION_MATRIX_COLOURS, getCorrelationLabelDisplayedText } from '@/utils/correlation-matrix';

// Types
import ComparisonHolding from '@/types/comparison-holding';
import CorrelationData from '@/types/correlation-data';

type Props = {
  holdings: ComparisonHolding[];
  correlation?: CorrelationData[];
  onClick?: (event: React.MouseEvent) => void;
};

class CorrelationChart extends React.PureComponent<Props> {
  colorScale = chroma.scale(CORRELATION_MATRIX_COLOURS);

  renderTable = () => {
    const { holdings, correlation } = this.props;
    const rows = [];
    const footerColumns = [<td key={0} />];

    if (!correlation) {
      return null;
    }

    // Filter duplicates
    const uniqueCorrelation = correlation.filter(c => c.holdings[0] !== c.holdings[1]);
    const uniqueHoldings = holdings.filter((holdingItem, i, arr) => arr.map(value => value.id).indexOf(holdingItem.id) === i);

    for (const holding of uniqueHoldings.slice(1).reverse()) {
      footerColumns.push(
        <td key={holding.id} className="correlation-x-label">
          <Tooltip message={holding.name ? formatLocalizedString(holding.name) : undefined}>
            <span className="correlation-label">{getCorrelationLabelDisplayedText(holding)}</span>
          </Tooltip>
        </td>,
      );
    }

    for (let i = 0; i < uniqueHoldings.length - 1; i++) {
      const rowData = [];

      for (let j = uniqueHoldings.length - 1; j > i; j--) {
        const correlationPoint = uniqueCorrelation.find((c: any) => c.holdings.includes(uniqueHoldings[i].id) && c.holdings.includes(uniqueHoldings[j].id));

        if (correlationPoint) {
          rowData.push(correlationPoint);
        } else {
          rowData.push({
            holdings: [uniqueHoldings[i].id, uniqueHoldings[j].id],
            value: null,
          });
        }
      }

      rows.push(<CorrelationChartRow key={i} colorScale={this.colorScale} holdings={uniqueHoldings} rowHolding={uniqueHoldings[i]} data={rowData} />);
    }

    return (
      <table>
        <tbody>
          {rows.reverse()}
          <tr>{footerColumns}</tr>
        </tbody>
      </table>
    );
  };

  renderContent = () => {
    const { holdings, correlation } = this.props;

    // No correlation data if less than 2 holdings - show proper message.
    if (holdings && holdings.length < 2) {
      return <GenericEmptyState body={formatMessage('Charts.Correlation.SingleHolding')} />;
    }

    // Show proper message if no correlation data for selected period.
    if (!correlation || !correlation.length || !holdings) {
      return <GenericEmptyState body={formatMessage('Charts.Correlation.NoData')} />;
    }

    return this.renderTable();
  };

  render() {
    const { onClick } = this.props;

    return (
      <div className="correlation-chart" onClick={onClick}>
        {this.renderContent()}
      </div>
    );
  }
}

export default CorrelationChart;
